import { IntlFormatters } from 'react-intl'
import * as Yup from 'yup'
import {
  InputField,
  SelectField as SelectFieldMedkit,
  RadioGroupFieldV2,
  AutoSuggestField,
  isValidDate,
} from '@babylon/medkit'

import { TextInput, RadioGroup, PasswordInput } from '@babylon/web-platform-ui'
import PhoneNumber from 'awesome-phonenumber'
import {
  DateInputWrapper,
  PhoneCountryCodeField,
  GenderField,
} from '@/components'
import validationMessages from './validation.messages'
import { getState } from '@/config/redux'
import { Field } from '@/types/partnerConfig'
import { MAX_YEARS_FOR_DOB } from '@/constants'
import { selectRegionInfo } from '@/redux/regionInfo/selectors'

import withField from '../Forms/withField'
import { SelectFieldWrapper } from '../Forms/FormElementWrappers'

export type ValidationSchema =
  | Yup.StringSchema
  | Yup.DateSchema
  | Yup.MixedSchema

interface MedkitFieldConfig {
  Component: React.FC<any>
  validate?: ValidationSchema | ((field: Field) => ValidationSchema)
  validateMessage?: string
}

const defaultInputConfig: MedkitFieldConfig = {
  Component: InputField,
}

const TextInputField = withField(TextInput)
const RadioGroupField = withField(RadioGroup)
const PasswordInputField = withField(PasswordInput)
const wpDefaultInputConfig: MedkitFieldConfig = {
  Component: TextInputField,
}

// At least one lowercase, one uppercase and one number
const rPassword = /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/

const getFieldConfig = (
  type: string,
  translate: IntlFormatters['formatMessage'],
  useDnaV2Design: Boolean
): MedkitFieldConfig => {
  const mainConfig: { [key: string]: MedkitFieldConfig } = {
    email: {
      Component: useDnaV2Design ? TextInputField : InputField,
      validate: Yup.string()
        .required(translate(validationMessages.email))
        .email(translate(validationMessages.email)),
    },
    password: {
      Component: useDnaV2Design ? PasswordInputField : InputField,
      validate: Yup.string()
        .required(translate(validationMessages.password))
        .min(8, translate(validationMessages.passwordLength, { length: 8 }))
        .matches(rPassword, translate(validationMessages.passwordFormat)),
    },
    zipcode: {
      Component: useDnaV2Design ? TextInputField : InputField,
      validate: Yup.string()
        .nullable()
        .required(translate(validationMessages.zipcode))
        .matches(/^[0-9]{5}$/, translate(validationMessages.zipcodeFormat))
        .notOneOf(
          ['00000', '11111', '12345', '99999'],
          translate(validationMessages.zipcodeInvalid)
        ),
    },
    date: {
      Component: DateInputWrapper,
      validate: ({ props, required }: Field) => {
        let finalSchema = Yup.date().transform((value, originalValue) =>
          isValidDate(originalValue) ? value : undefined
        )

        if (required) {
          finalSchema = finalSchema
            .required(translate(validationMessages.date))
            .typeError(translate(validationMessages.date))
        }

        if (props?.minDate) {
          const minDate = new Date(props.minDate)
          finalSchema = finalSchema.min(
            props.minDate,
            translate(validationMessages.dateMin, {
              minDate: minDate.toLocaleDateString(),
            })
          )
        }

        if (props?.maxDate) {
          const maxDate = new Date(props.maxDate)
          finalSchema = finalSchema.max(
            props.maxDate,
            translate(validationMessages.dateMax, {
              maxDate: maxDate.toLocaleDateString(),
            })
          )
        }

        return finalSchema
      },
    },
    dateOfBirth: {
      Component: DateInputWrapper,
      validateMessage: translate(validationMessages.dateOfBirth),
      validate: (field: Field) => {
        const state = getState()
        const { age_of_consent: ageOfConsent } = selectRegionInfo(state)

        const maxDate =
          field.props?.maxDate ||
          new Date(
            new Date().setFullYear(new Date().getFullYear() - ageOfConsent!)
          )
        const minDate =
          field.props?.minDate ||
          new Date(
            new Date().setFullYear(new Date().getFullYear() - MAX_YEARS_FOR_DOB)
          )
        const maxDateMessage = field.props?.maxDate
          ? translate(validationMessages.dateOfBirth)
          : translate(validationMessages.dateOfBirthAgeOfConsent, {
              ageOfConsent,
            })

        return (
          Yup.date()
            // For some reason Yup can't properly build a date object.
            // So we transform it ourselves.
            .transform((value, originalValue) => new Date(originalValue))
            .typeError(translate(validationMessages.dateOfBirth))
            .required(translate(validationMessages.dateOfBirth))
            .max(maxDate, maxDateMessage)
            .min(
              minDate,
              translate(validationMessages.dateOfBirthAgeTooOld, {
                maxYearsForDateOfBirth: minDate.getFullYear(),
              })
            )
        )
      },
    },
    select: {
      Component: useDnaV2Design ? SelectFieldWrapper : SelectFieldMedkit,
    },
    autoSuggest: {
      Component: AutoSuggestField,
    },
    radio: {
      Component: useDnaV2Design ? RadioGroupField : RadioGroupFieldV2,
    },
    gender: {
      Component: GenderField,
    },
    phoneCountryCode: {
      Component: PhoneCountryCodeField,
    },
    tel: {
      Component: useDnaV2Design ? TextInputField : InputField,
      validate: Yup.string()
        .test(
          'validPhone',
          translate(validationMessages.phone_number_invalid),
          function (input: string) {
            try {
              const pn = new PhoneNumber(
                `${this.parent.phone_country_code} ${input}`
              )

              return pn.isValid()
            } catch {
              return false
            }
          }
        )
        .required(),
    },
  }

  return (
    mainConfig[type] ||
    (useDnaV2Design ? wpDefaultInputConfig : defaultInputConfig)
  )
}

export default getFieldConfig
